import { useCallback, useEffect, useReducer } from 'react';

export type UseListNavigationAction =
    | { type: 'RESET'; payload: { length?: number } }
    | { type: 'PREV' }
    | { type: 'NEXT' }
    | { type: 'UPDATE'; payload: { cursor?: number } };

export type UseListNavigationState = {
    cursor: number;
    length: number;
};

const reducer =
    () =>
    (
        state: UseListNavigationState,
        action: UseListNavigationAction,
    ): UseListNavigationState => {
        switch (action.type) {
            case 'RESET':
                return { ...state, ...action.payload, cursor: -1 };
            case 'PREV':
                return { ...state, cursor: Math.max(0, state.cursor - 1) };
            case 'NEXT':
                return {
                    ...state,
                    cursor: Math.min(state.length - 1, state.cursor + 1),
                };
            case 'UPDATE':
                return { ...state, ...action.payload };
        }
    };

export type UseListNavigationProps<T> = {
    list: T[];
};

export const useListNavigation = <T>({ list }: UseListNavigationProps<T>) => {
    const defaultCursor = -1;
    const [state, dispatch] = useReducer(reducer(), {
        cursor: defaultCursor,
        length: list.length,
    });

    const next = useCallback(() => dispatch({ type: 'NEXT' }), []);
    const prev = useCallback(() => dispatch({ type: 'PREV' }), []);
    const reset = useCallback(
        () => dispatch({ type: 'RESET', payload: { length: list.length } }),
        [list.length],
    );
    const update = useCallback((payload: { cursor?: number }) => {
        dispatch({ type: 'UPDATE', payload });
    }, []);

    useEffect(
        () => dispatch({ type: 'RESET', payload: { length: list.length } }),
        [list.length],
    );

    return {
        selected: state.cursor,
        reset,
        update,
        next,
        prev,
    };
};
